import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MessageService } from '@igo2/core/message';
import { Feature, IgoMap } from '@igo2/geo';
import { MapState } from '@igo2/integration';
import { Constantes } from 'src/app/shared/constantes';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { EntityRecord, EntityTableButton, EntityTableColumnRenderer, EntityTableTemplate } from '@igo2/common/entity';
import { LanguageService } from '@igo2/core/language';
import { TransactionProprieteAmendementDto } from 'src/app/shared/models/transaction-propriete-amendement.dto';
import { RechercheRdeService } from 'src/app/shared/services/recherche-rde.service';
import * as olstyle from 'ol/style';

@Component({
    selector: 'app-transactions-amendements',
    templateUrl: './transactions-amendements.component.html',
    styleUrls: ['./transactions-amendements.component.scss'],
    imports: [
        ReactiveFormsModule,
        TranslateModule,
        MatFormFieldModule,
        MatDatepickerModule,
        MatButtonModule,
        MatInputModule,
    ]
})
export class TransactionsAmendementsComponent implements OnInit {
  propertyKeyAndTitle: { [key: string]: string };
  template: EntityTableTemplate; 
  templateHistory: EntityTableTemplate; 
  style: olstyle.Style;
  
  layerid: string = 'RDETransactionsProprietesAmendements';
  historyLayerid: string = 'RDETransactionsProprietesAmendementsHistorique';
  buttonClicked:boolean  = false;

  public form: UntypedFormGroup;
  messagesDeValidation: { [key: string]: string } = {};
  get map(): IgoMap {
    return this.mapState.map;
  }
  regexValidatorNumeroTransactionAmendement = /^(?:\d{1,2} \d{3} \d{3}|\d{1,8})$/;;

  constructor(
    private rechercheRdeService: RechercheRdeService,
    private formBuilder: UntypedFormBuilder,
    private messageService: MessageService,
    private languageService: LanguageService,   
    private mapState: MapState,
    private cdRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
      const actionHandlers: Record<string,(row: any, record?: EntityRecord<object>) => void > = {
      details: (row, record) => {
        if (record?.state?.selected) {
          this.rechercheRdeService.obtenirFicheDescriptiveTPA(row).subscribe({
            next: (blob: Blob) => {
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'Fiche - Transactions de propriété et amendements.pdf';
              a.click();
              window.URL.revokeObjectURL(url); // Clean up
            },
            error: (err) => {
              console.error('Erreur lors du téléchargement du PDF', err);
            }
          });
        }
      },

      getHistoricalData: (row, record) => {
        if (record?.state?.selected) {
          this.rechercheRdeService.obtenirHistoriqueTPA(row).subscribe({
            next: (history:TransactionProprieteAmendementDto[]) => {
            if(history.length > 0 ){
              this.rechercheRdeService.addSearchResultsToMap(
                  this.historyLayerid, 
                  this.rechercheRdeService.formatFeaturesForTable('transactionsAmendements', history, true),
                  this.cdRef,
                  this.templateHistory,
                  this.style
                )
              } else {
                this.messageService.info(this.messagesDeValidation.listeVide);
              }
            },
            error: (err) => {
              console.error('Erreur lors de la reception historique', err);
            }
          });
        }
      }
    }

    this.template = this.rechercheRdeService.getEntityTableTemplate('transactionsAmendements',actionHandlers);

    const historyActionHandlers: Record<string,(row: any, record?: EntityRecord<object>) => void > = {
      details: (row, record) => {
        if (record?.state?.selected) {
          this.rechercheRdeService.obtenirFicheDescriptiveTPA(row, true).subscribe({
            next: (blob: Blob) => {
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'Fiche - Transactions de propriété et amendements.pdf';
              a.click();
              window.URL.revokeObjectURL(url); // Clean up
            },
            error: (err) => {
              console.error('Erreur lors du téléchargement du PDF', err);
            }
          });
        }
      }
    };
    this.templateHistory = this.rechercheRdeService.getEntityTableTemplate('transactionsAmendements', historyActionHandlers);

    this.form = this.formBuilder.group({
      numeroTransactionAmendement: ["", [Validators.pattern(this.regexValidatorNumeroTransactionAmendement)]],
      dateDebut: new FormControl<Date | null>(null),
      dateFin: new FormControl<Date | null>(null)
    }, { validators: [this.validerUnChampRempli.bind(this), this.validerDateDebutFin.bind(this)] });

    this.form.valueChanges.subscribe(() => {
      if (this.form.valid) {
        this.buttonClicked = false;
      }
    });

    this.messagesDeValidation.dateDebutSuperieurFin = Constantes.MessageApplicatif.DateFinSuperieurDateDebut_15E;
    this.messagesDeValidation.listeVide = Constantes.MessageApplicatif.AucunElement_6I;
    this.messagesDeValidation.messageTechnique = Constantes.MessageApplicatif.CoucheIndisponible_16T;
    this.messagesDeValidation.grilleVide = Constantes.MessageApplicatif.AucunElement_6I;
    this.messagesDeValidation.erreurCouche = Constantes.MessageApplicatif.CoucheIndisponible_16T;
    this.messagesDeValidation.obligatoireNumero = Constantes.MessageApplicatif.ValeurObligatoire_1E
      .replace("{0}", this.getTitle('transactionsAmendements.numeroTransactionAmendement'));        
  }

  onRechercher() {
    this.buttonClicked = true;
    this.rechercher();
  }
  rechercher() {
    const numeroTPA = this.form.get("numeroTransactionAmendement").value;
    const dateFin = this.form.get("dateFin").value;
    const dateDebut = this.form.get("dateDebut").value;
    const indicateurHistorique = false;
    const statutTransaction = "TR";
    const statutTraitement = "TM";
    
    this.rechercheRdeService.obtenirListeTransactionsProprietesAmendements( 
      statutTransaction, 
      statutTraitement,
      indicateurHistorique,
      numeroTPA,
      dateDebut, 
      dateFin
    )
    .subscribe({
      next: reponse => {
        if (reponse?.length > 0){
          this.rechercheRdeService.addSearchResultsToMap(
            this.layerid, 
            this.rechercheRdeService.formatFeaturesForTable('transactionsAmendements', reponse),
            this.cdRef,
            this.template,
            this.style
          )
        } else {
          this.messageService.info(this.messagesDeValidation.listeVide);
        }
      },
      error: error => {
        this.messageService.error(this.messagesDeValidation.erreurCouche);
        console.log(error);
      }
    });
  }
  validerUnChampRempli(controlGroup: FormGroup): { [key: string]: any } | null {
    const hasValue =
      controlGroup.controls["dateDebut"].value ||
        controlGroup.controls["numeroTransactionAmendement"].value
        ? true
        : false;
    return hasValue ? null : { requiredAtLeastOne: true };
  }

  validerDateDebutFin(controlGroup: FormGroup): { [key: string]: any } | null {
    const champDateDebut = controlGroup.controls["dateDebut"].value;
    const champDateFin = controlGroup.controls["dateFin"].value;
    if (champDateDebut && champDateFin) {
      const dateDebut = new Date(champDateDebut);
      const dateFin = new Date(champDateFin);
      if (dateDebut > dateFin) {
        this.messageService.error(this.messagesDeValidation.dateDebutSuperieurFin.replace('{0}', ''));
        return { dateDebutFinErreur: { message: this.messagesDeValidation.dateDebutSuperieurFin.replace('{0}', '') } };
      }
    }
    return null;
  }

  respecterSyntax(event: any) {
    let valeur = event.target.value;
    valeur = valeur.replace(/[^ 0-9]/g, '');
    valeur = valeur.replace(/(\d{2})(\d{3})(\d{3})/, '$1 $2 $3');
    this.form.patchValue({ numeroTransactionAmendement: valeur });
  }

  getTitle(key: string) {
      return this.languageService.translate.instant(`sigt.integration.rechercheRde.form.${key}`);
  }
}
